import { useState, useEffect, useCallback, useRef, type MutableRefObject } from 'react';

interface UseProtectedCloseProps {
    /** Controls whether the modal is mounted/open; reopening resets internal state. */
    isOpen: boolean;
    /**
     * True when there are unsaved changes. First close attempt only warns; second attempt within
     * the timeout confirms and closes.
     */
    shouldWarn: boolean;
    /** Called once closing is permitted (after confirmation or when clean). */
    onClose: () => void;
    /** Hard lock to block any closing (e.g., while submitting). Default: true. */
    canClose?: boolean;
    /** Enable shake animation on the first blocked attempt. Default: true. */
    enableShake?: boolean;
    /**
     * Milliseconds the "confirm discard" state stays active. After it expires the user must
     * perform two attempts again. Default: 5000.
     */
    confirmationTimeout?: number;
}

function clearTimeoutRef(ref: MutableRefObject<ReturnType<typeof setTimeout> | null>) {
    if (ref.current) {
        clearTimeout(ref.current);
        ref.current = null;
    }
}

export function useProtectedClose({
    isOpen,
    shouldWarn,
    onClose,
    canClose = true,
    enableShake = true,
    confirmationTimeout = 5000,
}: UseProtectedCloseProps) {
    const [closeAttempted, setCloseAttempted] = useState(false);
    const [isShaking, setIsShaking] = useState(false);

    const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearConfirmationTimer = useCallback(() => {
        clearTimeoutRef(confirmationTimerRef);
    }, []);

    const resetConfirmationState = useCallback(() => {
        setCloseAttempted(false);
        clearConfirmationTimer();
    }, [clearConfirmationTimer]);

    const clearShakeTimer = useCallback(() => {
        clearTimeoutRef(shakeTimerRef);
    }, []);

    const resetShakeState = useCallback(() => {
        setIsShaking(false);
        clearShakeTimer();
    }, [clearShakeTimer]);

    const resetAll = useCallback(() => {
        resetConfirmationState();
        resetShakeState();
    }, [resetConfirmationState, resetShakeState]);

    // Clear any pending timer when the hook unmounts.
    useEffect(
        () => () => {
            clearConfirmationTimer();
            clearShakeTimer();
        },
        [clearConfirmationTimer, clearShakeTimer]
    );

    // Auto-reset when there are no unsaved changes (next close should succeed in one attempt).
    useEffect(() => {
        if (!shouldWarn) {
            resetAll();
        }
    }, [shouldWarn, resetAll]);

    // Reset state every time the modal opens; prevents stale warning/timeout from prior session.
    useEffect(() => {
        if (isOpen) {
            resetAll();
        }
    }, [isOpen, resetAll]);

    const startConfirmationWindow = useCallback(() => {
        setCloseAttempted(true);

        if (enableShake) {
            setIsShaking(true);
            clearShakeTimer();
            shakeTimerRef.current = setTimeout(resetShakeState, 300);
        }

        // Expire the confirmation window after X ms so a later attempt requires two steps again.
        clearConfirmationTimer();
        confirmationTimerRef.current = setTimeout(resetConfirmationState, confirmationTimeout);

        return false;
    }, [
        clearConfirmationTimer,
        clearShakeTimer,
        confirmationTimeout,
        enableShake,
        resetConfirmationState,
        resetShakeState,
    ]);

    const attemptClose = useCallback(() => {
        if (!canClose) return false;

        // 1. No unsaved changes: close immediately.
        if (!shouldWarn) {
            onClose();
            return true;
        }

        if (closeAttempted) {
            // 2. Confirmed within timeout: close.
            clearConfirmationTimer();
            onClose();
            return true;
        }

        // 3. First attempt with unsaved changes: warn and start confirmation window.
        return startConfirmationWindow();
    }, [
        canClose,
        shouldWarn,
        closeAttempted,
        onClose,
        startConfirmationWindow,
        clearConfirmationTimer,
    ]);

    return {
        attemptClose,
        isShaking,
        showWarning: shouldWarn && closeAttempted,
    };
}
